import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { DateToLocal } from "components/dates";
import { ShowHide } from "components/layout";
import { Tabs } from "components/navigation";
import { Modal, ModalSize, NotificationToaster } from "components/notifications";
import { EmployeeLicenceCheckDetails, EmployeeLicenceCheckEntitlements } from "pages/employee/edit/licence-checking/shared";
import { EmployeeLicenceCheckEndorsements } from "pages/employee/edit/licence-checking/shared/EmployeeLicenceCheckEndorsements";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { LicenceCheckingService } from "services";

export function EmployeeLicenceCheckPreviousCheckModal({
    employeeId,
    checkId,
    onModalClose,
    licenceCheckOptions = {},
    historicCheckDetails,
    onNextClick,
    onPrevClick,
    canGoToNextCheck,
    canGoToPrevCheck
}) {

    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const tabs = [
        { key: "DETAILS", title: "Details" },
        { key: "ENDORSEMENTS", title: "Endorsements" },
        { key: "ENTITLEMENTS", title: "Entitlements" }
    ];

    const [selectedTab, setSelectedTab] = useState(tabs[0].key);
    const [tabList, setTabList] = useState([]);

    const [latestCheck, setLatestCheck] = useState(null);
    const [checkExists, setCheckExists] = useState(true);

    function onTabClick(tabIndex) {
        setSelectedTab(tabs[tabIndex].key);
    }

    useEffect(() => {

        setTabList(tabs.map((tab) => {
            return {
                ...tab,
                selected: tab.key === selectedTab,
            }
        }));

    }, [selectedTab]);

    useEffect(() => {

        if (employeeId == null) {
            return;
        }

        const hasCheck = checkId != null;

        setShowModal(hasCheck);
        setCheckExists(true);

        if (!hasCheck) {
            return;
        }

        setLoading(true);

        LicenceCheckingService.getLicenceCheck(employeeId, checkId).then(function (response) {
            setLatestCheck(response);
            setCheckExists(response.licence?.status != null);
        }, function () {
            NotificationToaster.show(Intent.DANGER, "Failed to get previous check. Please close the modal and try again.");
        }).finally(function () {
            setLoading(false);
        })

    }, [checkId, employeeId]);


    return (
        <Modal
            isOpen={showModal}
            onClose={onModalClose}
            size={ModalSize.LARGE}
            fixedHeight
            fixedHeightPercentage={70}
            title={(
                <>
                    <h2>Licence Check on <DateToLocal format="Do MMMM YYYY">{historicCheckDetails?.date ?? "-"}</DateToLocal></h2>
                    <Tabs tabs={tabList} onClick={onTabClick} loading={loading} disabled={!checkExists} />
                </>
            )}
            footer={(
                <div className="inline-items inline-items-space">
                    <Button text="Previous Check" intent="primary" disabled={!canGoToPrevCheck} onClick={() => { onPrevClick(checkId) }}></Button>
                    <Button text="Next Check" intent="primary" disabled={!canGoToNextCheck} onClick={() => { onNextClick(checkId) }}></Button>
                </div>
            )}
        >

            <div className="spacer-bottom">

                <ShowHide
                    evaluator={checkExists}
                    show={(
                        <>
                            <ShowHide
                                evaluator={selectedTab === 'DETAILS'}
                                show={(
                                    <EmployeeLicenceCheckDetails
                                        latestCheck={latestCheck}
                                        isLatestCheck={false}
                                        gridSize={4}
                                        historicCheckDetails={historicCheckDetails}
                                        loading={loading}
                                    />
                                )}
                            />

                            <ShowHide
                                evaluator={selectedTab === 'ENDORSEMENTS'}
                                show={(
                                    <EmployeeLicenceCheckEndorsements
                                        endorsements={latestCheck?.endorsements ?? []}
                                        loading={loading}
                                    />
                                )}
                            />

                            <ShowHide
                                evaluator={selectedTab === 'ENTITLEMENTS'}
                                show={(
                                    <EmployeeLicenceCheckEntitlements
                                        entitlements={latestCheck?.entitlements ?? []}
                                        options={licenceCheckOptions?.entitlements ?? []}
                                    />
                                )}
                            />
                        </>
                    )}
                    hide={(
                        <>
                            <h3>Check is no longer available</h3>
                            <p>The check you are trying to view is no longer available, either because the employee left your organisation or requested to have their data deleted.</p>
                        </>
                    )}
                />


            </div>
        </Modal >
    );
}

EmployeeLicenceCheckPreviousCheckModal.propTypes = {
    checkId: PropTypes.number,
    employeeId: PropTypes.string.isRequired,
    onModalClose: PropTypes.func.isRequired,
    licenceCheckOptions: PropTypes.object,
    historicCheckDetails: PropTypes.object,
    onNextClick: PropTypes.func,
    onPrevClick: PropTypes.func,
    canGoToNextCheck: PropTypes.bool,
    canGoToPrevCheck: PropTypes.bool
};